import { Component, ViewChild } from '@angular/core';
import { ProjectGuardService } from '@app/components/cleanui/system/Guard/project.guard';
import { combineLatest, Observable } from 'rxjs';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { ActivatedRoute, Router } from '@angular/router';
import { participantPages } from '@app/pages/participant/participants.routes';
import { WorkflowRandomisationRestService } from '@app/services/rest/workflow-randomisation-rest.service';
import { ActiveParticipantService } from '@app/services/participants/active-participant.service';
import { ProjectService } from '@app/services/project/project.service';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { WorkflowService } from '@workflow/services/workflow.service';
import { IParticipant, RandomizationWorkflowItem } from '@interfaces/interfaces';
import { ModalDirective } from '@app/directives/modal/modal.directive';
import { WorkflowRestService } from '@app/services/rest/workflow-rest.service';
import { get } from 'lodash';

@Component({
	selector: 'workflow-randomization',
	templateUrl: './workflow-randomization.component.html',
	styleUrls: ['./workflow-randomization.component.scss'],
	providers: [ProjectGuardService],
})

export class WorkflowRandomizationComponent {
	@ViewChild('resetRandomization')
	public randomizationReset: ModalDirective<boolean>;
	@ViewChild('confirmRandomization')
	public randomizationConfirm: ModalDirective<boolean>;
	
	workflowItem$: Observable<RandomizationWorkflowItem>;
	backToParticipantSplitButton: SplitButtonElement = {
		title: 'workflow.cancel',
		type: 'default',
		icon: 'X',
		callback: () => this.showParticipantPage(),
	};
	randomizationButton: SplitButtonElement = {
		title: 'actions.randomization.start',
		type: 'primary',
		icon: 'columns',
		callback: () => this.doRandomization(),
	};
	resetRandomizationButton: SplitButtonElement = {
		title: 'actions.randomization.reset',
		type: 'default',
		icon: 'trash-2',
		callback: () => this.resetRandomization(),
	};
	isRandomizable$: Observable<boolean>;
	isFinished$: Observable<boolean>;
	allocatedGroup$: Observable<string | null>;
	
	participant$: Observable<IParticipant>;
	
	constructor(
		private readonly activeParticipantService: ActiveParticipantService,
		private readonly projectService: ProjectService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly restService: WorkflowRandomisationRestService,
		private readonly workflowRestService: WorkflowRestService,
		private readonly workflowService: WorkflowService,
	) {
		this.participant$ = this.activeParticipantService.participant$;
		
		const wfItemID$ = this.route.paramMap.pipe(map(params => params.get('itemId')));
		
		this.workflowItem$ = combineLatest([
			wfItemID$,
			workflowService.workflow$,
		]).pipe(
			filter(([wfItemId]) => !!wfItemId),
			map(([wfItemId, wf]) => wf?.items?.find(item => item.id === wfItemId) as RandomizationWorkflowItem),
		);
		this.isRandomizable$ = wfItemID$.pipe(
			switchMap((itemID) => combineLatest([
				this.restService.isItemRandomizable(this.projectService.activeProject.project_id, itemID),
				workflowService.workflowStatus$.pipe(map(status => status?.status[itemID]?.completed))
			])),
			map(([projectPossible, participantCompleted]) => projectPossible && !participantCompleted)
		);
		this.isFinished$ = wfItemID$.pipe(
			switchMap(wfItemID => workflowService.workflowStatus$.pipe(map(status => status?.status[wfItemID]?.completed))),
		);
		this.allocatedGroup$ = combineLatest([this.participant$, this.workflowItem$]).pipe(
			map(([participant, item]) => get<string>(participant.data, item.participantValueKey, null)),
		);
	}
	
	private showParticipantPage(): void {
		this.router
			.navigate([participantPages.stammDaten.base()])
			.catch(console.error);
	}
	
	private async getProjectParameter(): Promise<string[]> {
		const [participantID, itemID] = await combineLatest([
			this.participant$,
			this.workflowItem$,
		]).pipe(
			map(([iParticipant,workflowItem]) => { return [iParticipant.id, workflowItem?.id]; }),
			first(),
		).toPromise();
		
		return [
			this.projectService.activeProject.project_id,
			participantID,
			itemID,
		];
	}
	
	async doRandomization(): Promise<void> {
		try {
			if (await this.randomizationConfirm.show()) {
				const [projectID, participantID, itemID] = await this.getProjectParameter();
				
				await this.restService.doItemRandomization(projectID, participantID, itemID).toPromise();
				await this.activeParticipantService.refreshParticipant().catch(console.error);
				
				this.showParticipantPage();
			}
		} catch (e) {
			console.error(e);
		}
	}
	async resetRandomization(): Promise<void> {
		if (!(await this.randomizationReset.show())) return;
		
		const [projectID, participantID, itemID] = await this.getProjectParameter();
		
		await this.restService.resetItemRandomization(projectID, participantID, itemID).toPromise().catch(console.error);
		await this.activeParticipantService.refreshParticipant();
		
		this.workflowService.changed();
	}
}